import { useCapabilityRegistryContext } from '@mobile-mapping/providers';
import type { ControlMenuItemType } from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { MarkupCapability } from '@trimble/web3d-capabilities';
import { MarkupsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, batch, createMemo, createSignal } from 'solid-js';

export interface UseMainToolbarMarkupsResult {
  readonly markupMenuItems: Accessor<ControlMenuItemType[]>;
  readonly isMarkupActive: Accessor<boolean>;
  deactivateMarkup(): void;
}

/**
 * @intent Owns main-toolbar markup dropdown state, mirroring the former pane
 * markup selector without reintroducing pane-local toolbar controls.
 */
export const useMainToolbarMarkups = (): UseMainToolbarMarkupsResult => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const markupCapability = capabilityRegistry.get(MarkupCapability);
  const markupsProvider = capabilityRegistry.get(MarkupsCapabilityProvider);
  const [selectedMarkupItemId, setSelectedMarkupItemId] = createSignal<
    string | undefined
  >();

  const isMarkupActive = createMemo(
    (): boolean => markupCapability()?.isActive() ?? false
  );

  const activeMarkupItemId = createMemo((): string | undefined =>
    isMarkupActive() ? selectedMarkupItemId() : undefined
  );

  const deactivateMarkup = (): void =>
    batch(() => {
      markupCapability()?.deactivate();
      setSelectedMarkupItemId(undefined);
    });

  const startMarkupTool = (itemId: string, start: () => void): void =>
    batch(() => {
      if (activeMarkupItemId() === itemId) {
        deactivateMarkup();
        return;
      }
      setSelectedMarkupItemId(itemId);
      start();
    });

  const markupMenuItems = createMemo((): ControlMenuItemType[] => [
    {
      id: 'cloud-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.cloudMarkup'),
      icon: 'tc-icon-markup-cloud',
      selected: activeMarkupItemId() === 'cloud-markup',
      onClick: () =>
        startMarkupTool('cloud-markup', () =>
          markupCapability()?.startCloudMarkupCreation()
        ),
    },
    {
      id: 'arrow-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.arrowMarkup'),
      icon: 'tc-icon-markup-arrow',
      selected: activeMarkupItemId() === 'arrow-markup',
      onClick: () =>
        startMarkupTool('arrow-markup', () =>
          markupCapability()?.startArrowMarkupCreation()
        ),
    },
    {
      id: 'line-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.lineMarkup'),
      icon: 'tc-icon-markup-line',
      selected: activeMarkupItemId() === 'line-markup',
      onClick: () =>
        startMarkupTool('line-markup', () =>
          markupCapability()?.startLineMarkupCreation()
        ),
    },
    {
      id: 'text-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.textMarkup'),
      icon: 'tc-icon-markup-text-active',
      selected: activeMarkupItemId() === 'text-markup',
      onClick: () =>
        startMarkupTool('text-markup', () =>
          markupCapability()?.startTextMarkupCreation()
        ),
    },
    // Draw markup intentionally omitted (NUMARK-880): freeline creation is
    // broken on point clouds because drag is intercepted by orbit navigation.
    // Re-add once fixed in MarkupCapability.
    {
      id: 'markups-divider',
      type: 'divider' as const,
    },
    {
      id: 'delete-markups',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.deleteAllMarkups'),
      icon: 'tc-icon-delete',
      replayable: false,
      onClick: () => markupsProvider()?.removeAllMarkups(),
    },
  ]);

  return {
    markupMenuItems,
    isMarkupActive,
    deactivateMarkup,
  };
};
